import React, { useCallback, useEffect, useRef, useState } from 'react';

interface ScrollProgressBarProps {
  containerRef?: React.RefObject<HTMLElement>;
  className?: string;
}

export const ScrollProgressBar: React.FC<ScrollProgressBarProps> = ({ containerRef, className }) => {
  const [progress, setProgress] = useState(0);
  const [shouldShow, setShouldShow] = useState(false);
  const rafRef = useRef<number>();

  const calculateProgress = useCallback(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
    }

    rafRef.current = requestAnimationFrame(() => {
      const element = containerRef?.current || window;

      if (element === window) {
        const windowHeight = window.innerHeight;
        const documentHeight = document.documentElement.scrollHeight;
        const scrollTop = Math.ceil(window.pageYOffset || document.documentElement.scrollTop);

        const totalScrollableHeight = documentHeight - windowHeight;

        // Check if content exceeds viewport
        setShouldShow(documentHeight > windowHeight);

        if (totalScrollableHeight <= 0) {
          setProgress(100);
          return;
        }

        const rawProgress = (scrollTop / totalScrollableHeight) * 100;

        // Same logic as for element scrolling
        if (scrollTop >= totalScrollableHeight - 2 || rawProgress >= 99.5) {
          setProgress(100);
        } else {
          setProgress(Math.min(100, Math.round(rawProgress * 10) / 10));
        }
      } else if (element instanceof HTMLElement) {
        // Get scroll dimensions
        const scrollTop = Math.ceil(element.scrollTop);
        const scrollHeight = element.scrollHeight;
        const clientHeight = element.clientHeight;

        // Check if content exceeds container viewport
        setShouldShow(scrollHeight > clientHeight);

        // If content doesn't overflow, we're at 100%
        if (scrollHeight <= clientHeight) {
          setProgress(100);
          return;
        }

        // Maximum scrollable distance
        const maxScroll = scrollHeight - clientHeight;

        // Calculate progress with proper rounding
        const rawProgress = (scrollTop / maxScroll) * 100;

        // If we're within 2 pixels of the bottom, consider it 100%
        // This accounts for sub-pixel rendering and rounding errors
        if (scrollTop >= maxScroll - 2 || rawProgress >= 99.5) {
          setProgress(100);
        } else {
          // Use Math.min to ensure we never exceed 100%
          setProgress(Math.min(100, Math.round(rawProgress * 10) / 10));
        }
      }
    });
  }, [containerRef]);

  useEffect(() => {
    const element = containerRef?.current || window;

    // Initial calculation with a small delay to ensure content is rendered
    const initialTimer = setTimeout(() => {
      calculateProgress();
    }, 100);

    // Throttle function for better performance
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          calculateProgress();

          // Additional check for bottom detection
          const el = containerRef?.current || window;
          if (el === window) {
            const scrollY = window.pageYOffset || document.documentElement.scrollTop;
            const windowHeight = window.innerHeight;
            const documentHeight = document.documentElement.scrollHeight;

            // If user has scrolled to within 5px of bottom, force 100%
            if (scrollY + windowHeight >= documentHeight - 5) {
              setProgress(100);
            }
          } else if (el instanceof HTMLElement) {
            // If scrolled to within 5px of bottom, force 100%
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 5) {
              setProgress(100);
            }
          }

          ticking = false;
        });
        ticking = true;
      }
    };

    if (element === window) {
      window.addEventListener('scroll', handleScroll, { passive: true });
      window.addEventListener('resize', calculateProgress, { passive: true });
    } else {
      element.addEventListener('scroll', handleScroll, { passive: true });
    }

    // Add ResizeObserver to handle content changes
    let resizeObserver: ResizeObserver | null = null;
    if (element !== window && element instanceof HTMLElement) {
      resizeObserver = new ResizeObserver(() => {
        calculateProgress();
      });
      resizeObserver.observe(element);

      // Also observe the first child if it exists (content container)
      const contentElement = element.firstElementChild;
      if (contentElement) {
        resizeObserver.observe(contentElement);
      }
    }

    // Also observe document body for content changes when using window scroll
    if (element === window && document.body) {
      resizeObserver = new ResizeObserver(() => {
        calculateProgress();
      });
      resizeObserver.observe(document.body);
    }

    return () => {
      clearTimeout(initialTimer);

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      if (element === window) {
        window.removeEventListener('scroll', handleScroll);
        window.removeEventListener('resize', calculateProgress);
      } else {
        element.removeEventListener('scroll', handleScroll);
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      // No intersection observer to clean up in current implementation
    };
  }, [containerRef, calculateProgress]);

  // Don't render if content doesn't exceed viewport
  if (!shouldShow) {
    return null;
  }

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 100,
        height: '4px',
        backgroundColor: 'transparent',
      }}
      className={className}
    >
      <div
        style={{
          height: '100%',
          backgroundColor: '#2563eb',
          transition: 'width 150ms ease-out',
          width: `${progress}%`,
          boxShadow: progress > 0 ? '0 2px 4px rgba(0, 0, 0, 0.1)' : 'none',
        }}
      />
    </div>
  );
};
